import React from "react";
import {
  Divider,
  Grid,
  Paper,
  Stack,
  Switch,
  Typography,
  Box as MuiBox,
  Chip,
} from "@mui/material";
import { Box } from "@mui/system";
import { HighlightOffOutlined, ArrowForward } from "@mui/icons-material";
import { transactionStyle } from "./Dialog.styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useState, useMemo } from "react";
import ApiTableComponent from "../../../../../base/ApiTable.component";
import { POLL_CANOPY_STATUS } from "../../../../../../graphql/query/query";
import { useQuery } from "@apollo/client";

const VerificationProcessDialog = (props) => {
  const { payload, status, pull } = props;
  const [checked, setChecked] = useState(false);
  const [tableView, setTableView] = useState(false);

  const MoveToRawResponse = () => {
    setTableView(!checked);
    setChecked((prevState) => !prevState);
  };

  const { data: canopyStatus } = useQuery(POLL_CANOPY_STATUS, {
    variables: {
      pull_id: pull?.pullId,
    },
    skip: !pull?.pullId,
  });

  // Determine step statuses and find the first failed step
  const stepStatuses = useMemo(() => {
    const steps = [
      { key: "consent", label: "Privacy Consent", order: 1 },
      { key: "carriers", label: "Insurance Carriers", order: 2 },
      { key: "login", label: "Provider Login", order: 3 },
      { key: "webhook", label: "Canopy Webhook", order: 4 },
      { key: "pull", label: "Pull Insurance", order: 5 },
    ];

    let firstFailedIndex = -1;
    const statuses = steps.map((step, index) => {
      const stepStatus = status?.[step.key];

      // If a previous step failed, mark this as DNR
      if (firstFailedIndex !== -1 && index > firstFailedIndex) {
        return {
          ...step,
          status: "dnr",
          isSuccess: false,
          isFailed: false,
          isDNR: true,
        };
      }

      // Check if this step failed
      if (!stepStatus && firstFailedIndex === -1) {
        firstFailedIndex = index;
        return {
          ...step,
          status: "failed",
          isSuccess: false,
          isFailed: true,
          isDNR: false,
        };
      }

      // Step succeeded
      return {
        ...step,
        status: stepStatus ? "success" : "failed",
        isSuccess: !!stepStatus,
        isFailed: !stepStatus,
        isDNR: false,
      };
    });

    // Find the first failed step for the message
    const firstFailedStep = statuses.find((s) => s.isFailed);

    let responseMessage = "";
    const canopyStatusData = canopyStatus?.pollCanopyStatus;
    if (firstFailedStep) {
      switch (firstFailedStep.key) {
        case "consent":
          responseMessage = `Privacy Consent Failed\n\n- Accepting Privacy Consent\n- Privacy Consent Failed`;
          break;
        case "carriers":
          responseMessage = `Insurance Carriers Failed\n\n- Selected ${
            pull?.carrier || "carrier"
          }\n- Insurance Carriers Failed`;
          break;
        case "login":
          responseMessage = `Provider Login Failed\n\n- Logging into ${
            pull?.carrier || "carrier"
          }\n- Incorrect credentials.`;
          break;
        case "webhook":
          responseMessage = `EVENT TYPE: ${
            canopyStatusData?.event_type || "N/A"
          }\nSTATUS: ${canopyStatusData?.status || "N/A"}\nDATA: ${
            canopyStatusData?.data || "N/A"
          }`;
          break;
        case "pull":
          responseMessage = `Canopy response:\nEVENT TYPE: ${
            canopyStatusData?.event_type || "N/A"
          }\nSTATUS: ${canopyStatusData?.status || "N/A"}\nDATA: ${
            canopyStatusData?.data || "N/A"
          }\n\nPull response:\n ${
            firstFailedStep.status === "failed"
              ? "Pull failed due to unexpected error. "
              : "Pull completed successfully."
          }`;
          break;
        default:
          responseMessage = `${firstFailedStep.label} Failed`;
      }
    }

    return {
      steps: statuses,
      firstFailedStep: firstFailedStep
        ? { step: firstFailedStep, message: responseMessage }
        : null,
    };
  }, [status, pull]);

  return (
    <>
      <Divider />
      {payload ? (
        <Box component={Paper}>
          <Box>
            <Grid
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              //height={"5vh"}
              //overflow={"hidden"}
            >
              <Box mt={1} mr={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography color={!checked ? "primary" : "text.primary"}>
                    Table View
                  </Typography>
                  <Switch
                    checked={checked}
                    size="small"
                    onChange={MoveToRawResponse}
                    inputProps={{ "aria-label": "ant design" }}
                  />
                  <Typography color={checked ? "primary" : "text.primary"}>
                    Raw response
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Box>
          {!tableView ? (
            <Box>
              <ApiTableComponent payload={payload} />
            </Box>
          ) : (
            <Box sx={{ m: "16px" }} height={"500px"} overflow={"auto"}>
              <pre
                style={{
                  background: "white",
                  padding: "16px",
                  display: { xs: "block", sm: "flex" },
                  justifyContent: "center",
                  width: "100%",
                  whiteSpace: "pre",
                }}
              >
                {JSON.stringify(payload, null, 2)}
              </pre>
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Box sx={{ m: "16px" }}>
            <Box>
              <Typography
                sx={{
                  ...transactionStyle.typography,
                  marginBottom: "24px",
                  fontSize: "20px",
                }}
              >
                Initiating Verification Process
              </Typography>
            </Box>

            <Box sx={{ mb: 3, position: "relative", overflowX: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "nowrap",
                  minWidth: "max-content",
                }}
              >
                {stepStatuses.steps.map((step, index) => {
                  const isLast = index === stepStatuses.steps.length - 1;
                  const prevStep =
                    index > 0 ? stepStatuses.steps[index - 1] : null;

                  const getChipLabel = () => {
                    if (step.isDNR) return "DNR";
                    if (step.isFailed) return "Failed";
                    return "Success";
                  };

                  const getChipColor = () => {
                    if (step.isDNR) return "default";
                    if (step.isFailed) return "error";
                    return "success";
                  };

                  return (
                    <React.Fragment key={step.key}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          flex: "0 0 auto",
                          minWidth: { xs: "80px", sm: "90px", md: "100px" },
                        }}
                      >
                        <Stack
                          direction="column"
                          alignItems="center"
                          spacing={1}
                          sx={{ py: 1 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 36,
                              height: 36,
                            }}
                          >
                            {step.isDNR ? (
                              <HighlightOffOutlined
                                sx={{
                                  fontSize: 24,
                                  color: "grey.400",
                                }}
                              />
                            ) : step.isSuccess ? (
                              <CheckCircleOutlineIcon
                                sx={{
                                  fontSize: 24,
                                  color: "success.main",
                                }}
                              />
                            ) : (
                              <HighlightOffOutlined
                                sx={{
                                  fontSize: 24,
                                  color: "error.main",
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            textAlign="center"
                            fontWeight={600}
                            sx={{
                              fontSize: "0.75rem",
                              color: step.isDNR
                                ? "text.disabled"
                                : "text.primary",
                            }}
                          >
                            {step.label}
                          </Typography>
                          <Chip
                            label={getChipLabel()}
                            color={getChipColor()}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              opacity: step.isDNR ? 0.6 : 1,
                              fontSize: "0.7rem",
                              height: "20px",
                            }}
                          />
                        </Stack>
                      </Box>
                      {!isLast && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            px: { xs: 0.5, sm: 1, md: 1.5 },
                            mt: -2,
                          }}
                        >
                          <ArrowForward
                            sx={{
                              fontSize: { xs: 18, sm: 20, md: 22 },
                              color: (theme) => {
                                if (prevStep?.isFailed || prevStep?.isDNR) {
                                  return "error.main";
                                }
                                return prevStep?.isSuccess
                                  ? "success.main"
                                  : "grey.400";
                              },
                            }}
                          />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>

            {/* Final Response Section */}
            <>
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "bold", color: "text.primary" }}
              >
                Final Response
              </Typography>
              <Box
                sx={{
                  color: "text.secondary",
                  bgcolor: "background.paper",
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {stepStatuses.firstFailedStep ? (
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-line",
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {stepStatuses.firstFailedStep.message}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    All verification steps completed successfully.
                  </Typography>
                )}
              </Box>
            </>
          </Box>
        </Box>
      )}
    </>
  );
};

export default VerificationProcessDialog;
